import { getEffectiveDate } from "../../../../utils/effectiveDate";

export type RecentActivityPeriodUi =
  | "last7d"
  | "last30d"
  | "last90d"
  | "custom";

function formatRangeDate(d: Date): string {
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Calendar date in local TZ (no time drift). */
function truncateToLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function parseYmd(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (
    !Number.isFinite(y) ||
    !Number.isFinite(mo) ||
    !Number.isFinite(day)
  ) {
    return null;
  }
  const d = new Date(y, mo, day);
  if (d.getFullYear() !== y || d.getMonth() !== mo || d.getDate() !== day) {
    return null;
  }
  return d;
}

/**
 * Human-readable bill/invoice activity range (Revenue & Expense recent-activity APIs).
 * Preset periods mirror RevenueController::recentActivity / ExpenseController (end = effective “today”).
 */
export function getRecentRevenueActivityDateRangeSubtext(
  period: RecentActivityPeriodUi,
  customStartDate: string,
  customEndDate: string,
): string {
  if (period === "custom") {
    if (customStartDate?.trim() && customEndDate?.trim()) {
      const start = parseYmd(customStartDate);
      const end = parseYmd(customEndDate);
      if (start && end) {
        return `${formatRangeDate(start)} – ${formatRangeDate(end)}`;
      }
    }
    return "Select a start and end date, then Apply.";
  }

  const end = truncateToLocalDay(getEffectiveDate());
  const start = new Date(end);
  const daysBack =
    period === "last7d" ? 7 : period === "last90d" ? 90 : 30;
  start.setDate(start.getDate() - daysBack);

  return `${formatRangeDate(start)} – ${formatRangeDate(end)}`;
}

/** Alias for expense recent activity (same date rules as revenue). */
export const getRecentActivityDateRangeSubtext =
  getRecentRevenueActivityDateRangeSubtext;
